// src/excel/builders/peak/buildWorkbook.js
// XLSX-билдер для сервиса Peak loads

export function buildPeakWorkbook(XLSX, params) {
  const {
    t,
    shortcode,
    stationName,
    period,
    date1,
    date2,
    reportData = {},
    peakInfo,
    serviceName
  } = params || {};

  const wb = XLSX.utils.book_new();
  const ws = {};
  const merges = [];

  const brandText = (t && t('listeners.brand_line_excel')) || 'Radiomonitor.Net';
  const serviceTitle = serviceName || (t ? t('peak.title') : 'Peak loads');
  const stationTitle = stationName || shortcode || '';
  const stripIcon = (text) => (text || '').replace(/^📅\s*/u, '').trim();

  const periodLines = [];
  if (period === 'day') {
    periodLines.push(`${stripIcon(t && t('peak.date'))} ${date1 || ''}`.trim());
  } else if (period === 'week') {
    periodLines.push(`${stripIcon(t && t('peak.end_of_week'))} ${date2 || ''}`.trim());
  } else if (period === 'month') {
    periodLines.push(`${stripIcon(t && t('peak.end_of_period'))} ${date1 || ''}`.trim());
  }

  const metaRows = [
    [serviceTitle],
    ...(stationTitle ? [[stationTitle]] : []),
    ...periodLines.map((line) => [line]),
    ...(peakInfo ? [[peakInfo]] : []),
    ['']
  ];

  let currentRow = 0;

  const brandRow = [[brandText]];
  XLSX.utils.sheet_add_aoa(ws, brandRow, { origin: { r: currentRow, c: 0 } });
  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 1 } });
  const brandAddr = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
  ws[brandAddr] = ws[brandAddr] || {};
  ws[brandAddr].s = {
    font: { bold: true, sz: 20, color: { rgb: '1F2A5A' } },
    fill: { fgColor: { rgb: 'EEF2FF' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };

  currentRow += 2;

  XLSX.utils.sheet_add_aoa(ws, metaRows, { origin: { r: currentRow, c: 0 } });
  for (let i = 0; i < metaRows.length; i += 1) {
    merges.push({ s: { r: currentRow + i, c: 0 }, e: { r: currentRow + i, c: 1 } });
  }

  const encode = XLSX.utils.encode_cell;
  const serviceAddr = encode({ r: currentRow, c: 0 });
  if (ws[serviceAddr]) {
    ws[serviceAddr].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  if (stationTitle) {
    const stationAddr = encode({ r: currentRow + 1, c: 0 });
    if (ws[stationAddr]) {
      ws[stationAddr].s = {
        font: { bold: true, sz: 16, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  const infoStartIndex = 1 + (stationTitle ? 1 : 0);
  for (let i = infoStartIndex; i < metaRows.length - 1; i += 1) {
    const addr = encode({ r: currentRow + i, c: 0 });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { sz: 11, color: { rgb: '4F4F4F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  currentRow += metaRows.length;

  const labels = Array.isArray(reportData.labels) ? reportData.labels : [];
  const values = Array.isArray(reportData.data) ? reportData.data : [];
  const HEAD_LABEL = (t && t(`peak.${period}`)) || (period ? period : 'Label');
  const HEAD_VALUE = (t && t('peak.chart_label')) || 'Listeners';

  const tableRows = labels.map((label, idx) => [
    label ?? '',
    typeof values[idx] === 'number' ? values[idx] : Number(values[idx] || 0)
  ]);

  const tableBlock = [
    [HEAD_LABEL, HEAD_VALUE],
    ...tableRows
  ];

  XLSX.utils.sheet_add_aoa(ws, tableBlock, { origin: { r: currentRow, c: 0 } });

  const headerRowIndex = currentRow;
  const totalRowIndex = currentRow + tableBlock.length - 1;

  const headerStyle = {
    font: { bold: true, color: { rgb: '333333' } },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };

  const borderStyle = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  for (let c = 0; c < 2; c += 1) {
    const addr = encode({ r: headerRowIndex, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  for (let r = headerRowIndex + 1; r <= totalRowIndex; r += 1) {
    for (let c = 0; c < 2; c += 1) {
      const addr = encode({ r, c });
      if (!ws[addr]) continue;
      ws[addr].s = {
        ...(ws[addr].s || {}),
        ...borderStyle,
        alignment: { horizontal: c === 1 ? 'right' : 'left', vertical: 'center' },
        font: { sz: 11, color: { rgb: '1F1F1F' } }
      };
      if (c === 1 && typeof ws[addr].v === 'number') {
        ws[addr].t = 'n';
        ws[addr].z = '#,##0';
      }
    }
  }

  ws['!cols'] = [
    { wch: 22 },
    { wch: 18 }
  ];

  ws['!merges'] = merges;

  if (tableRows.length > 0) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: totalRowIndex, c: 1 }
      })
    };
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Peak');
  return wb;
}

export default buildPeakWorkbook;
